import { Paging } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { AppFeedbackInjection } from "features/appFeedback/di/AppFeedbackInjection";
import { AppFeedbackRequestEntity } from "features/appFeedback/domain/entities/AppFeedbackRequestEntity";
import { AppFeedbackResponseEntity } from "features/appFeedback/domain/entities/AppFeedbackResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

// ─── Input ───────────────────────────────────────────────────────────────────
class AppFeedbackInput implements Disposable {
  public fetchAppFeedbacks = new BehaviorSubject<AppFeedbackRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchAppFeedbackDetail = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.fetchAppFeedbacks.complete();
    this.fetchAppFeedbackDetail.complete();
  }
}

// ─── Output ──────────────────────────────────────────────────────────────────
class AppFeedbackOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<AppFeedbackResponseEntity>>([]);
  public savedDetail = new BehaviorSubject<AppFeedbackResponseEntity | null>(null);
  public pagination = new BehaviorSubject<Paging>({ page: 0, limit: 10, total: 0 });
  public onError = new BehaviorSubject<string>("");
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.pagination.complete();
    this.onError.complete();
    this.loading.complete();
    this.detailLoading.complete();
  }
}

// ─── ViewModel ───────────────────────────────────────────────────────────────
@injectable()
export class AppFeedbackViewModel extends BaseViewModel<
  AppFeedbackInput,
  AppFeedbackOutput
> {
  constructor() {
    super(new AppFeedbackInput(), new AppFeedbackOutput());
  }

  private appFeedbackUseCase = AppFeedbackInjection.getUseCase();

  protected override transform(): void {
    // Fetch list
    this.skipFirstInitial(this.input.fetchAppFeedbacks, async (params) => {
      try {
        this.output.loading.next(true);
        this.output.savedList.next([]);
        const res = await this.appFeedbackUseCase.getAppFeedbacks(params);
        if (res?.data) {
          this.output.savedList.next(res.data);
          this.output.pagination.next(res.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.savedList.next([]);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.loading.next(false);
      }
    });

    // Fetch detail
    this.skipFirstInitial(this.input.fetchAppFeedbackDetail, async (id) => {
      if (!id) return;
      try {
        this.output.detailLoading.next(true);
        const res = await this.appFeedbackUseCase.getAppFeedbackDetail(id);
        if (res?.data) {
          this.output.savedDetail.next(res.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.output.detailLoading.next(false);
      }
    });
  }
}
